// src/components/layout/SubscriptionLapsedBanner.js

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { billingBlockText } from "../../utils/billingBlock";
import "../../scss/demo.scss";

const EMAIL = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

// Banner text rendered as plain text, with any email address made a mailto link.
export function linkifyEmails(text) {
  return text.split(EMAIL).map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={`mailto:${part}`}>
        {part}
      </a>
    ) : (
      part
    )
  );
}

/**
 * Persistent billing banner. The backend User Plan process decides, so this only renders what it reports:
 * - `billingMessage`: the admin's message (admin Subscription tab), shown to everyone in the org, before and after
 *   the block date, with no button (those orgs are handled by sales);
 * - otherwise `subscriptionLapsed` (status NOT_ACTIVE or the block date passed): the default expired text, with the
 *   sales email as a mailto link and, for supervisors, a "Subscription" button that opens My Plan.
 * For an org marked "paid outside Stripe" the backend reports only what the admin set: `billingMessage`, and
 * `subscriptionLapsed` once the block date passes.
 * Modeled on ImpersonationBanner.js's placement/styling.
 */
function SubscriptionLapsedBanner() {
  const { t } = useTranslation();
  const { userPlan, hasGroup } = useAuth();
  const navigate = useNavigate();

  // Only supervisors can reach My Plan; everyone else is told who can.
  const isSupervisor = hasGroup("supervisor");
  const text = billingBlockText(userPlan, isSupervisor, t);
  if (!text) return null;

  const canRenew = !userPlan.billingMessage && isSupervisor;

  return (
    <div className="demo-banner subscription-lapsed-banner" role="alert">
      <div className="demo-banner-left">
        <i className="notranslate material-icons-outlined">warning_amber</i>
        <span>{linkifyEmails(text)}</span>
      </div>
      {canRenew && (
        <button className="demo-banner-exit" onClick={() => navigate("/my-plan")}>
          {t("subscriptionLapsed.subscription")}
        </button>
      )}
    </div>
  );
}

export default SubscriptionLapsedBanner;
